import { InvestmentSummaryCardStyles } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/components/InvestmentSummaryCard/InvestmentSummaryCard.styles.tsx';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { Button, Flex, Menu, Text, Tooltip, useMantineTheme } from '@mantine/core';
import type { InvestmentDetailsPendingActions } from '@dealstack/shared/lib/generated';
import { InvestmentWorkflowStatus } from '@dealstack/shared/lib/generated';
import { StatusBadge } from '@dealstack/frontend/shared/components/StatusBadge/StatusBadge.tsx';
import { getInvestmentStatusBadge } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/components/investmentStatusBadge.ts';
import {
	DATE_FORMATS,
	formatDateToUI,
	formatStringDateToUI,
	isIsoDateInPastUtcDay,
} from '@dealstack/shared/lib/format/dates/date.ts';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import { LONG_DASH } from '@dealstack/frontend/shared/other/react/specialSigns.tsx';
import { InvestmentButtonMenu } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/components/InvestmentButtonMenu/InvestmentButtonMenu.tsx';
import type { PendingActionOption } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/components/InvestmentSummaryCard/InvestmentSummaryCard.types.ts';

interface Props {
	title: string;
	investmentStatus: InvestmentWorkflowStatus;
	createdOnDate: string;
	releaseDate: string | null;
	createdBy: string | null;
	onStartInvestment?: () => void;
	isStartInvestmentPending?: boolean;
	onApprove?: () => void;
	isApprovePending?: boolean;
	onMakeDraft?: () => void;
	isMakeDraftPending?: boolean;
	onRelease?: () => void;
	isReleasePending?: boolean;
	targetStartDate: string | null;
	targetCompletionDate: string | null;
	pendingActions?: InvestmentDetailsPendingActions;
	onReviewKyc?: () => void;
	onReviewDocuments?: () => void;
	onReviewConsideration?: () => void;
}

export const InvestmentSummaryCard = ({
	title,
	investmentStatus,
	createdOnDate,
	releaseDate,
	createdBy,
	onStartInvestment,
	isStartInvestmentPending,
	onApprove,
	isApprovePending,
	onMakeDraft,
	isMakeDraftPending,
	onRelease,
	isReleasePending,
	targetStartDate,
	targetCompletionDate,
	pendingActions,
	onReviewKyc,
	onReviewDocuments,
	onReviewConsideration,
}: Props) => {
	const tMipManage = useTranslationMipManage();
	const theme = useMantineTheme();

	const createdOn = formatDateToUI(new Date(createdOnDate), DATE_FORMATS.CALENDAR_DATE);
	const releaseOn = releaseDate
		? formatDateToUI(new Date(releaseDate), DATE_FORMATS.CALENDAR_DATE)
		: null;

	const startDateExpectedInFuture =
		investmentStatus === InvestmentWorkflowStatus.OPEN ||
		investmentStatus === InvestmentWorkflowStatus.DEFINITION_COMPLETE;
	const showStartDateWarning =
		startDateExpectedInFuture && !!targetStartDate && isIsoDateInPastUtcDay(targetStartDate);

	const completionDateExpectedInFuture = investmentStatus !== InvestmentWorkflowStatus.RELEASED;
	const showCompletionDateWarning =
		completionDateExpectedInFuture &&
		!!targetCompletionDate &&
		isIsoDateInPastUtcDay(targetCompletionDate);

	const statusBadge = getInvestmentStatusBadge(investmentStatus, tMipManage, 'dark');

	const pendingActionCandidates: (PendingActionOption | false)[] = pendingActions
		? [
				pendingActions.kyc && {
					key: 'kyc',
					label: tMipManage('investment_summary_card.actions.review_kyc'),
					icon: 'verified-v3',
					onClick: onReviewKyc,
				},
				pendingActions.documents.count > 0 && {
					key: 'documents',
					label:
						pendingActions.documents.count === 1
							? tMipManage('investment_summary_card.actions.review_document')
							: tMipManage('investment_summary_card.actions.review_documents'),
					icon: 'document-v3',
					onClick: onReviewDocuments,
				},
				pendingActions.consideration && {
					key: 'consideration',
					label: tMipManage('investment_summary_card.actions.review_consideration'),
					icon: 'money-v3',
					onClick: onReviewConsideration,
				},
			]
		: [];
	const pendingActionOptions = pendingActionCandidates.filter(
		(option): option is PendingActionOption => option !== false
	);
	const [primaryPendingAction, ...secondaryPendingActions] = pendingActionOptions;

	return (
		<InvestmentSummaryCardStyles>
			<div className="content">
				<div className="meta">
					<Flex mb={12} justify="space-between" align="center">
						<Text fz={12} fw={600} c="gray.6" tt="uppercase">
							{tMipManage('investment_summary_card.meta.title')}
						</Text>

						<StatusBadge
							text={statusBadge.text}
							color={statusBadge.color}
							textColor={statusBadge.textColor}
						/>
					</Flex>

					<Text size="lg" c="gray.0" fw={600} tt="uppercase">
						{title}
					</Text>
				</div>

				<Flex className="rows" direction="column" gap="0.5rem">
					<Flex justify="space-between" align="center">
						<Text c="gray.8">
							{tMipManage('investment_summary_card.rows.created_by')}
						</Text>

						<Text fw={600} c="gray.4">
							{createdBy ?? LONG_DASH}
						</Text>
					</Flex>

					<Flex justify="space-between" align="center">
						<Text c="gray.8">
							{tMipManage('investment_summary_card.rows.created_on')}
						</Text>

						<Text fw={600} c="gray.4">
							{createdOn}
						</Text>
					</Flex>

					{targetStartDate && (
						<Flex justify="space-between" align="center">
							<Text c="gray.8">
								{tMipManage('investment_summary_card.rows.target_start_date')}
							</Text>

							<Flex align="center" gap="0.5rem">
								{showStartDateWarning && (
									<Tooltip
										label={tMipManage(
											'investment_summary_card.rows.target_start_date_warning'
										)}
										withArrow
										multiline
										w={240}
									>
										<span>
											<Icon
												variant="warning"
												size="lg"
												color={theme.colors.orange[6]}
											/>
										</span>
									</Tooltip>
								)}

								<Text fw={600} c="gray.4">
									{formatStringDateToUI(
										targetStartDate,
										DATE_FORMATS.CALENDAR_DATE
									)}
								</Text>
							</Flex>
						</Flex>
					)}

					{targetCompletionDate && (
						<Flex justify="space-between" align="center">
							<Text c="gray.8">
								{tMipManage('investment_summary_card.rows.target_completion_date')}
							</Text>

							<Flex align="center" gap="0.5rem">
								{showCompletionDateWarning && (
									<Tooltip
										label={tMipManage(
											'investment_summary_card.rows.target_completion_date_warning'
										)}
										withArrow
										multiline
										w={240}
									>
										<span>
											<Icon
												variant="warning"
												size="lg"
												color={theme.colors.orange[6]}
											/>
										</span>
									</Tooltip>
								)}

								<Text fw={600} c="gray.4">
									{formatStringDateToUI(
										targetCompletionDate,
										DATE_FORMATS.CALENDAR_DATE
									)}
								</Text>
							</Flex>
						</Flex>
					)}
				</Flex>
			</div>
			<div
				className={`actions ${investmentStatus === InvestmentWorkflowStatus.RELEASED ? 'released' : ''}`}
			>
				{investmentStatus === InvestmentWorkflowStatus.OPEN && onApprove && (
					<Flex>
						<Button
							variant="filled"
							color="blue.6"
							styles={{
								root: {
									borderRadius: '0.25rem 0 0 0.25rem',
								},
							}}
							fullWidth
							leftSection={<Icon variant="circle-check" size="xl" />}
							onClick={onApprove}
							loading={isApprovePending}
						>
							<Text fw={600} c="white" ml="0.5rem">
								{tMipManage('investment_summary_card.actions.approve_investment')}
							</Text>
						</Button>

						<InvestmentButtonMenu color={theme.colors.blue[6]}>
							{onStartInvestment && (
								<Menu.Item
									onClick={onStartInvestment}
									disabled={isStartInvestmentPending}
								>
									<Flex gap="0.5rem" align="center" style={{ padding: '0.5rem' }}>
										<Text size="sm">
											{tMipManage(
												'investment_summary_card.actions.start_investment'
											)}
										</Text>
									</Flex>
								</Menu.Item>
							)}
						</InvestmentButtonMenu>
					</Flex>
				)}

				{investmentStatus === InvestmentWorkflowStatus.DEFINITION_COMPLETE &&
					onStartInvestment && (
						<Flex>
							<Button
								variant="filled"
								color="blue.6"
								styles={{
									root: {
										borderRadius: '0.25rem 0 0 0.25rem',
									},
								}}
								fullWidth
								leftSection={<Icon variant="paper-plane" size="xl" />}
								onClick={onStartInvestment}
								loading={isStartInvestmentPending}
							>
								<Text fw={600} c="white" ml="0.5rem">
									{tMipManage('investment_summary_card.actions.start_investment')}
								</Text>
							</Button>

							<InvestmentButtonMenu color={theme.colors.blue[6]}>
								{onMakeDraft && (
									<Menu.Item onClick={onMakeDraft} disabled={isMakeDraftPending}>
										<Flex
											gap="0.5rem"
											align="center"
											style={{ padding: '0.5rem' }}
										>
											<Text size="sm">
												{tMipManage(
													'investment_summary_card.actions.make_draft'
												)}
											</Text>
										</Flex>
									</Menu.Item>
								)}
							</InvestmentButtonMenu>
						</Flex>
					)}

				{investmentStatus === InvestmentWorkflowStatus.IN_PROGRESS && (
					<Flex direction="column" gap="0.5rem">
						{primaryPendingAction ? (
							<Flex>
								<Button
									variant="filled"
									color="blue.6"
									styles={{
										root: {
											borderRadius: secondaryPendingActions.length
												? '0.25rem 0 0 0.25rem'
												: '0.25rem',
										},
									}}
									fullWidth
									leftSection={
										<Icon
											variant={primaryPendingAction.icon}
											size="xl"
											fillStroke={false}
											color="white"
										/>
									}
									onClick={primaryPendingAction.onClick}
								>
									<Text fw={600} c="white" ml="0.5rem">
										{primaryPendingAction.label}
									</Text>
								</Button>

								{secondaryPendingActions.length > 0 && (
									<InvestmentButtonMenu color={theme.colors.blue[6]}>
										{secondaryPendingActions.map((action) => (
											<Menu.Item key={action.key} onClick={action.onClick}>
												<Flex
													gap="0.5rem"
													align="center"
													style={{ padding: '0.5rem' }}
												>
													<Icon variant={action.icon} size="lg" />
													<Text size="sm">{action.label}</Text>
												</Flex>
											</Menu.Item>
										))}
									</InvestmentButtonMenu>
								)}
							</Flex>
						) : (
							pendingActions && (
								<Text fw={600} size="sm" ta="center" py="0.5rem" c="orange.2">
									{tMipManage(
										'investment_summary_card.actions.awaiting_investor'
									)}
								</Text>
							)
						)}
					</Flex>
				)}

				{investmentStatus === InvestmentWorkflowStatus.COMPLETE && onRelease && (
					<Button
						variant="filled"
						color="blue.6"
						fullWidth
						leftSection={<Icon variant="circle-check" size="xl" />}
						onClick={onRelease}
						loading={isReleasePending}
					>
						<Text fw={600} c="white" ml="0.5rem">
							{tMipManage('investment_summary_card.actions.release', {
								defaultValue: 'Release investment',
							})}
						</Text>
					</Button>
				)}

				{investmentStatus === InvestmentWorkflowStatus.RELEASED && (
					<Text fw={600} c="gray.0" className="released-text">
						{tMipManage('investment_summary_card.actions.released', {
							releaseDate: releaseOn,
						})}
					</Text>
				)}
			</div>
		</InvestmentSummaryCardStyles>
	);
};
